import {
  addNoteResponseSchema,
  callbackSlotsResponseSchema,
  callSequenceResponseSchema,
  createLeadResponseSchema,
  createTagResponseSchema,
  isEmptyFilter,
  leadCardResponseSchema,
  leadFieldsResponseSchema,
  leadListResponseSchema,
  leadOwnerOptionsResponseSchema,
  leadStatusesResponseSchema,
  leadVisibilityResponseSchema,
  leadWriteResponseSchema,
  municipalitiesResponseSchema,
  ownerCountsResponseSchema,
  publishLeadFieldsResponseSchema,
  reassignLeadsResponseSchema,
  tagsResponseSchema,
  taskResponseSchema,
  timelinePageSchema,
  whatsAppOptOutResponseSchema,
  type AddNoteBody,
  type ChangeOwnerBody,
  type CreateCallbackBody,
  type CreateLeadBody,
  type LeadCardResponse,
  type LeadListQuery,
  type LeadListResponse,
  type LeadPatchBody,
  type LeadRow,
  type LeadVisibilityBody,
  type LeadWriteResponse,
  type MarkAnsweredBody,
  type PublishLeadFieldsBody,
  type RescheduleTaskBody,
  type SequenceActionBody,
  type TaskView,
  type TimelineFilter,
  type TimelineItem,
  type TimelinePage,
} from "@gioia365/contracts";
import { infiniteQueryOptions, queryOptions, useMutation, useQueryClient, type InfiniteData, type QueryClient, type QueryKey } from "@tanstack/react-query";
import { useCallback, useRef } from "react";
import { ApiRequestError, apiRequest } from "../api/http";
import { useTab } from "../api/queries";
import { conversationKeys } from "../conversations/conversation-keys";
import { commercialViewOf, tabKey, type TabContext, type TabKey } from "../session/tab-context";
import { refreshSmartListMenu } from "../smart-lists/menu-refresh";
import { refreshWhileSettling } from "../telephony/call-display";
import { taskKeys } from "../tasks/task-keys";
import { tableLayoutQuery } from "./columns/layout-queries";
import { LEADS_TABLE_SCOPE } from "./columns/table-layout";
import { leadCatalogQuery } from "./filters/filter-queries";

// Le query dei Lead (piano M2, Parte B). Le chiavi cominciano come quelle di M1b, `["scheda", tabKey(tab), …]`, così il
// cambio di azienda le toglie tutte insieme; dopo `"leads"` viene la vista: l'appartenenza osservata della Vista
// Commerciale, oppure `null`. Con la vista cambia il perimetro dei Lead, quindi due viste non condividono mai una riga
// di cache; l'header della vista lo manda `apiRequest` dalla scheda (Task 22), e la vista delle chiavi viene dalla
// stessa scheda (`useLeadView`). Portano la vista anche le altre letture che contano soltanto i Lead che si vedono: i
// campi della scheda con il loro uso e i Lead per owner della pagina Team.

/** Le righe di una pagina dell'elenco (piano M2 D5: pagine da 100 per `offset`). */
export const LEAD_PAGE_SIZE = 100;

export const leadKeys = {
  list: (tab: TabKey, query: LeadListQuery, view: string | null) => ["scheda", tab, "leads", "list", view, query] as const,
  card: (tab: TabKey, leadId: string, view: string | null) => ["scheda", tab, "leads", "card", view, leadId] as const,
  timeline: (tab: TabKey, leadId: string, filter: string, q: string, view: string | null) =>
    ["scheda", tab, "leads", "timeline", view, leadId, filter, q] as const,
  /** Le pagine dell'elenco in ogni vista, ricerca e filtro: il prefisso di `list`. */
  lists: (tab: TabKey) => ["scheda", tab, "leads", "list"] as const,
  /** Le Timeline di un Lead in una vista, con ogni filtro e ricerca: il prefisso di `timeline`. */
  timelines: (tab: TabKey, leadId: string, view: string | null) => ["scheda", tab, "leads", "timeline", view, leadId] as const,
  /** Le Timeline di ogni Lead in ogni vista: dopo «Segna tutti come letti», che non dice quali Lead ha toccato. */
  allTimelines: (tab: TabKey) => ["scheda", tab, "leads", "timeline"] as const,
};

/**
 * La prima pagina dell'indirizzo `/lead` senza parametri: ordinata per data di creazione, dalla più recente, e «Di chi»
 * scelto dal server (i miei per il livello 1, tutti per gli altri). La stessa chiave serve al prefetch e alla pagina.
 */
export const DEFAULT_LEAD_LIST_QUERY: LeadListQuery = { sort: "createdAt", direction: "desc", offset: 0, limit: LEAD_PAGE_SIZE };

function listPath(query: LeadListQuery): string {
  const params = new URLSearchParams();
  if (query.q !== undefined && query.q !== "") params.set("q", query.q);
  if (query.who !== undefined) params.set("who", query.who);
  // Nella query il filtro è la definizione già letta (il tipo d'uscita del contratto); al server va il suo JSON
  // (piano M3, D3). Il filtro vuoto non si manda.
  if (query.filter !== undefined && !isEmptyFilter(query.filter)) params.set("filter", JSON.stringify(query.filter));
  // I campi dell'azienda delle colonne visibili (piano M3 Task 6): i loro valori arrivano in `custom`.
  if (query.fields !== undefined && query.fields !== "") params.set("fields", query.fields);
  // «In Smart List» e «Provenienza» (LEAD-13), quando si vedono: le loro letture arrivano con le righe.
  if (query.include !== undefined && query.include !== "") params.set("include", query.include);
  params.set("sort", query.sort);
  params.set("direction", query.direction);
  params.set("offset", String(query.offset));
  params.set("limit", String(query.limit));
  return `/api/leads?${params.toString()}`;
}

/** Una pagina dell'elenco dei Lead (`GET /api/leads`): righe, totale del perimetro filtrato e «Di chi» applicato. */
export function leadListQuery(tab: TabContext, query: LeadListQuery, view: string | null) {
  return queryOptions({
    queryKey: leadKeys.list(tabKey(tab), query, view),
    queryFn: ({ signal }) => apiRequest("GET", listPath(query), { schema: leadListResponseSchema, tab, signal }),
    // Tornare all'elenco entro mezzo minuto (dalla scheda, dalla Home) mostra subito le righe senza rileggerle;
    // una creazione le fa rileggere da sé (`useCreateLead`).
    staleTime: 30_000,
  });
}

/**
 * Legge in anticipo la prima pagina di partenza dell'elenco, così il passaggio alla sezione «Lead» mostra subito le
 * righe (velocità: criterio di accettazione). La chiama la shell appena ha il contesto della scheda e al passaggio sulla
 * voce della rail, con la scheda della guardia: la chiave e l'header portano la stessa Vista Commerciale. Non rilegge
 * una pagina ancora fresca; un errore resta nella cache, e la pagina lo mostra. Insieme legge la vista delle colonne e
 * i campi dell'azienda (piano M3 Task 6): la tabella si apre subito con le colonne di chi guarda, senza passare da
 * quelle iniziali.
 */
export function prefetchLeadList(queryClient: QueryClient, tab: TabContext): Promise<void> {
  return Promise.all([
    queryClient.prefetchQuery(leadListQuery(tab, DEFAULT_LEAD_LIST_QUERY, commercialViewOf(tab))),
    queryClient.prefetchQuery(tableLayoutQuery(tab, LEADS_TABLE_SCOPE)),
    queryClient.prefetchQuery(leadCatalogQuery(tab)),
  ]).then(() => undefined);
}

/** L'appartenenza osservata della Vista Commerciale della scheda, oppure `null`: la stessa che l'header porta. */
export function useLeadView(): string | null {
  return commercialViewOf(useTab());
}

/** La scheda di un Lead (`GET /api/leads/:leadId`). */
export function leadCardQuery(tab: TabContext, leadId: string, view: string | null) {
  return queryOptions({
    queryKey: leadKeys.card(tabKey(tab), leadId, view),
    queryFn: ({ signal }) => apiRequest("GET", leadPath(leadId), { schema: leadCardResponseSchema, tab, signal }),
    // Il passaggio sul nome nell'elenco la legge in anticipo (`usePrefetchLeadCard`): per dieci secondi l'apertura la
    // mostra senza rileggerla, poi la rilegge in background mostrando intanto quella in cache.
    staleTime: 10_000,
  });
}

function leadPath(leadId: string): string {
  return `/api/leads/${encodeURIComponent(leadId)}`;
}

/**
 * Legge in anticipo la scheda di un Lead quando la persona sta per aprirla (il puntatore o il focus sul nome
 * nell'elenco), così si apre subito (velocità: criterio di accettazione). Un errore resta nella cache, e la scheda lo
 * mostra.
 */
export function usePrefetchLeadCard(): (leadId: string) => void {
  const queryClient = useQueryClient();
  const tab = useTab();
  const view = useLeadView();
  return useCallback(
    (leadId: string) => {
      void queryClient.prefetchQuery(leadCardQuery(tab, leadId, view));
      // La Timeline arriva con la scheda: la sua prima pagina, «Tutto» e senza ricerca, si legge insieme.
      void queryClient.prefetchInfiniteQuery(timelineQuery(tab, leadId, "all", "", view));
    },
    [queryClient, tab, view],
  );
}

// ---------------------------------------------------------------------------------------------------------------
// La Timeline del Lead (piano M2 Task 19): pagine a cursore, la nota nuova e l'appunto di una telefonata.

/** Gli elementi di una pagina della Timeline (il server ne accetta da 1 a 100, di partenza 50). */
export const TIMELINE_PAGE_SIZE = 50;

function timelinePath(leadId: string, filter: TimelineFilter, q: string, cursor: string | null): string {
  const params = new URLSearchParams();
  params.set("filter", filter);
  if (q !== "") params.set("q", q);
  if (cursor !== null) params.set("cursor", cursor);
  params.set("limit", String(TIMELINE_PAGE_SIZE));
  return `${leadPath(leadId)}/timeline?${params.toString()}`;
}

/**
 * La Timeline di un Lead (`GET /api/leads/:leadId/timeline`) con un filtro e una ricerca, a pagine: ogni pagina parte
 * dal cursore della precedente, e le pagine già lette restano. `q` è la ricerca già ripulita (vuota senza ricerca).
 */
export function timelineQuery(tab: TabContext, leadId: string, filter: TimelineFilter, q: string, view: string | null) {
  return infiniteQueryOptions({
    queryKey: leadKeys.timeline(tabKey(tab), leadId, filter, q, view),
    queryFn: ({ signal, pageParam }) => apiRequest("GET", timelinePath(leadId, filter, q, pageParam), { schema: timelinePageSchema, tab, signal }),
    initialPageParam: null as string | null,
    getNextPageParam: (last: TimelinePage) => last.nextCursor,
    // Come la scheda: aperta subito dopo il passaggio sul nome, senza rileggerla.
    staleTime: 10_000,
    // Una registrazione in preparazione arriva qualche minuto dopo la chiamata: la Timeline si rilegge finché ce n'è una
    // (minore di R2 per R3), così «In preparazione…» diventa «Da ascoltare» senza ricaricare la pagina; e finché una telefonata
    // è ancora viva o appena finita senza la sua registrazione (`refreshWhileSettling`).
    refetchInterval: (query) => refreshWhileSettling(query.state.data?.pages.flatMap((page) => page.items.filter((item) => item.kind === "call")) ?? []),
  });
}

type TimelineData = InfiniteData<TimelinePage, string | null>;

/** Il filtro e la ricerca di una chiave della Timeline (`leadKeys.timeline`). */
function timelineKeyParts(key: QueryKey): { filter: unknown; q: unknown } {
  return { filter: key[6], q: key[7] };
}

/**
 * Le note della Timeline (`POST /api/leads/:leadId/notes`): una nota nuova, o l'appunto di una telefonata (`callId`).
 * Una nota non si modifica né si cancella. Quando il server risponde, la nota compare subito in cima alle Timeline in
 * cache senza ricerca dove sta («Tutto» e «Note»), l'appunto sotto la sua telefonata ovunque la telefonata ci sia; una
 * Timeline con una ricerca si rilegge, perché la nota nuova potrebbe rispondere. Una lettura ancora in volo, partita
 * prima della nota, si rifà dopo, così non la cancella.
 */
export function useAddLeadNote(tab: TabContext, leadId: string, view: string | null) {
  const queryClient = useQueryClient();
  const tabPart = tabKey(tab);
  return useMutation({
    mutationFn: (body: AddNoteBody) => apiRequest("POST", `${leadPath(leadId)}/notes`, { schema: addNoteResponseSchema, body, tab }),
    onSuccess: async ({ note }) => {
      const prefix = ["scheda", tabPart, "leads", "timeline", view, leadId];
      const callId = note.callId;
      // L'appunto di una telefonata sta anche nella sua riga di «Conversazioni» (piano M4, Task 19).
      if (callId !== null) void queryClient.invalidateQueries({ queryKey: conversationKeys.all(tabPart) });
      for (const query of queryClient.getQueryCache().findAll({ queryKey: prefix })) {
        const key = query.queryKey;
        const { filter, q } = timelineKeyParts(key);
        const holds = callId === null ? filter === "all" || filter === "notes" : filter !== "notes" && filter !== "changes";
        if (!holds) continue;
        if (q !== "") {
          void queryClient.invalidateQueries({ queryKey: key, exact: true });
          continue;
        }
        const fetching = query.state.fetchStatus === "fetching";
        if (fetching) await queryClient.cancelQueries({ queryKey: key, exact: true });
        queryClient.setQueryData<TimelineData>(key, (data) => {
          if (!data) return data;
          if (callId === null) {
            const item: TimelineItem = { kind: "note", id: note.id, occurredAt: note.occurredAt, occurredAtKnown: true, actor: note.author, fromClose: false, body: note.body, originLabel: null };
            const [first, ...rest] = data.pages;
            if (!first || data.pages.some((page) => page.items.some((candidate) => candidate.kind === "note" && candidate.id === note.id))) return data;
            return { ...data, pages: [{ ...first, items: [item, ...first.items] }, ...rest] };
          }
          const memo = { id: note.id, body: note.body, occurredAt: note.occurredAt, actor: note.author };
          return {
            ...data,
            pages: data.pages.map((page) => ({
              ...page,
              items: page.items.map((candidate) =>
                candidate.kind === "call" && candidate.id === callId && !candidate.notes.some((existing) => existing.id === note.id)
                  ? { ...candidate, notes: [...candidate.notes, memo] }
                  : candidate,
              ),
            })),
          };
        });
        if (fetching) void queryClient.invalidateQueries({ queryKey: key, exact: true });
      }
    },
  });
}

/**
 * La riga di un Lead in una pagina dell'elenco già in cache, se c'è: mentre arriva la scheda, la testata si disegna
 * subito con nome, salone, status, tag e owner presi da lì.
 */
export function cachedLeadRow(queryClient: QueryClient, tab: TabKey, leadId: string): LeadRow | undefined {
  for (const [, page] of queryClient.getQueriesData<LeadListResponse>({ queryKey: ["scheda", tab, "leads", "list"] })) {
    const row = page?.rows.find((candidate) => candidate.id === leadId);
    if (row) return row;
  }
  return undefined;
}

/** Gli status che si scelgono, in ordine (`GET /api/lead-statuses`): il menu della testata. */
export function leadStatusesQuery(tab: TabContext) {
  return queryOptions({
    queryKey: ["scheda", tabKey(tab), "lead-statuses"],
    queryFn: async ({ signal }) => (await apiRequest("GET", "/api/lead-statuses", { schema: leadStatusesResponseSchema, tab, signal })).statuses,
    staleTime: 5 * 60_000,
  });
}

/** I tag non nascosti, in ordine alfabetico (`GET /api/tags`): la finestra dei tag. */
export function tagsQuery(tab: TabContext) {
  return queryOptions({
    queryKey: ["scheda", tabKey(tab), "tags"],
    queryFn: async ({ signal }) => (await apiRequest("GET", "/api/tags", { schema: tagsResponseSchema, tab, signal })).tags,
    staleTime: 60_000,
  });
}

/**
 * «Crea il tag «…»» (24/10/2026): `POST /api/tags` con il nome, con `manage_tags`. Il server restituisce il tag nuovo o quello che
 * c'era già con lo stesso nome; poi si rileggono i tag che si scelgono e le voci dei filtri (anche quelli delle Smart List), perché
 * il tag compaia subito nel filtro «Tag».
 */
export function useCreateTag(tab: TabContext) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (name: string) => apiRequest("POST", "/api/tags", { schema: createTagResponseSchema, body: { name }, tab }),
    onSuccess: ({ tag }) => {
      // Subito nella cache dei tag, così la scelta che lo nomina vale anche prima della rilettura.
      queryClient.setQueryData(tagsQuery(tab).queryKey, (current) => (current && !current.some((candidate) => candidate.id === tag.id) ? [...current, tag] : current));
      const part = tabKey(tab);
      for (const queryKey of [["scheda", part, "tags"], ["scheda", part, "leads", "filter-options"]]) void queryClient.invalidateQueries({ queryKey });
    },
  });
}

export type CardLead = LeadCardResponse["lead"];

/**
 * Una modifica dei dati della scheda: ciò che va al server e come la scheda la mostra subito, prima della risposta; con
 * `reloadAfter` la scheda si rilegge dopo, perché il server cambia anche parti che la risposta non porta (uno status che blocca
 * le chiamate chiude la sequenza e spegne «Chiama»).
 */
export type LeadEdit = { changes: LeadPatchBody["changes"]; optimistic: (lead: CardLead) => CardLead; reloadAfter?: boolean };

/**
 * Una scrittura della scheda rimasta in coda quando un'altra ha trovato la scheda cambiata da qualcun altro: non parte,
 * perché era stata pensata sui dati di prima. La scheda è già stata riletta. `cause` è il rifiuto che l'ha fermata.
 */
export class SupersededWriteError extends Error {
  constructor(cause?: unknown) {
    super("Lead write superseded by a reload", { cause });
    this.name = "SupersededWriteError";
  }
}

/**
 * Il Lead è cambiato altrove mentre la modifica partiva: il rifiuto di versione, oppure una scrittura in coda fermata da
 * quel rifiuto. La versione vale per tutto il Lead, quindi basta un cambio qualunque di un altro (uno status, un owner,
 * l'importazione). Chi ha scritto non perde nulla: la casella si riapre con la sua modifica, da rimandare con Invio.
 */
export function isVersionConflict(error: unknown): boolean {
  if (error instanceof SupersededWriteError) return isVersionConflict(error.cause);
  return error instanceof ApiRequestError && error.code === "version_conflict";
}

/**
 * I rifiuti che riguardano la scheda intera e non il campo: qualcun altro l'ha cambiata (versione), il modello o i
 * permessi sono cambiati, il Lead non c'è più. La scheda si rilegge e lo dice in cima; il campo torna com'è sul server.
 */
const CARD_LEVEL_CODES: ReadonlySet<string> = new Set(["version_conflict", "field_not_editable", "permission_denied", "lead_not_found"]);

export function isCardLevelError(error: unknown): boolean {
  return error instanceof SupersededWriteError || (error instanceof ApiRequestError && CARD_LEVEL_CODES.has(error.code));
}

type Write = {
  id: number;
  epoch: number;
  optimistic: (lead: CardLead) => CardLead;
  send: (version: number) => Promise<LeadWriteResponse>;
  /** Dopo un cambio di owner cambiano anche l'avviso «Questo Lead è di …» e i permessi: la scheda si rilegge. */
  reloadAfter?: boolean;
};

const unchanged = (lead: CardLead) => lead;

/**
 * Le scritture della scheda di un Lead: modifiche dei dati (`PATCH`), owner (`POST …/owner`), tag (`PUT …/tags`) e «Non inviare
 * WhatsApp» (`PUT …/whatsapp-opt-out`, piano M6, R1, Task 4b: la versione del Lead come `expectedVersion`; la risposta porta anche
 * il WhatsApp della scheda, che va subito in cache).
 *
 * - Ottimistiche: la modifica si vede subito; la scheda in cache è sempre l'ultima risposta del server con sopra le
 *   modifiche ancora in volo, nell'ordine in cui sono partite, così una risposta non cancella quelle che seguono.
 * - Una dopo l'altra (`scope`): ogni scrittura parte con la versione prodotta dalla precedente, e due modifiche fatte di
 *   seguito non si scontrano fra loro.
 * - Un rifiuto della scheda intera (`isCardLevelError`) la rilegge; le scritture ancora in coda non partono e tornano a
 *   chi le ha fatte (`SupersededWriteError`) quando la scheda riletta è già in cache; ogni altro rifiuto toglie soltanto
 *   la sua modifica. Il chiamante decide che cosa dire: dopo un conflitto di versione la riga riapre la casella con la
 *   modifica scritta, che Invio rimanda con la versione nuova (niente parte da solo).
 */
export function useLeadCardWrites(tab: TabContext, leadId: string, view: string | null) {
  const queryClient = useQueryClient();
  const tabPart = tabKey(tab);
  const state = useRef<{ confirmed: CardLead | null; pending: Write[]; epoch: number; counter: number; supersededBy: unknown }>({
    confirmed: null,
    pending: [],
    epoch: 0,
    counter: 0,
    supersededBy: undefined,
  });

  const mutation = useMutation({
    scope: { id: `lead-write:${tabPart}:${leadId}` },
    mutationFn: async (write: Write) => {
      if (write.epoch !== state.current.epoch) throw new SupersededWriteError(state.current.supersededBy);
      const version = state.current.confirmed?.version ?? queryClient.getQueryData<LeadCardResponse>(leadKeys.card(tabPart, leadId, view))?.lead.version;
      if (version === undefined) throw new SupersededWriteError();
      return write.send(version);
    },
    onMutate: async (write: Write) => {
      const key = leadKeys.card(tabPart, leadId, view);
      // Una lettura in volo non deve arrivare dopo e cancellare la modifica appena fatta.
      await queryClient.cancelQueries({ queryKey: key, exact: true });
      if (state.current.pending.length === 0) state.current.confirmed = queryClient.getQueryData<LeadCardResponse>(key)?.lead ?? null;
      state.current.pending.push(write);
      redraw();
    },
    onSuccess: (response: LeadWriteResponse, write: Write) => {
      settle(write);
      state.current.confirmed = response.lead;
      redraw();
      // L'elenco e la Timeline del Lead sono cambiati: si rileggono quando servono. Il Lead può entrare in una Smart List
      // o uscirne (status, owner, città, tag, campi): il menu rilegge numeri e novità.
      void queryClient.invalidateQueries({ queryKey: leadKeys.lists(tabPart) });
      void queryClient.invalidateQueries({ queryKey: leadKeys.timelines(tabPart, leadId, view) });
      refreshSmartListMenu(queryClient, tab);
      if (write.reloadAfter) void queryClient.invalidateQueries({ queryKey: leadKeys.card(tabPart, leadId, view), exact: true });
    },
    onError: async (error: unknown, write: Write) => {
      settle(write);
      if (error instanceof SupersededWriteError || !isCardLevelError(error)) {
        redraw();
        return;
      }
      // La scheda è cambiata altrove: le scritture ancora in coda non partono (ognuna torna a chi l'ha fatta, con il
      // rifiuto che l'ha fermata), e la scheda si rilegge prima che tornino.
      state.current.epoch += 1;
      state.current.supersededBy = error;
      state.current.pending = [];
      await queryClient.refetchQueries({ queryKey: leadKeys.card(tabPart, leadId, view), exact: true });
      state.current.confirmed = queryClient.getQueryData<LeadCardResponse>(leadKeys.card(tabPart, leadId, view))?.lead ?? null;
      redraw();
    },
  });

  function settle(write: Write) {
    state.current.pending = state.current.pending.filter((candidate) => candidate.id !== write.id);
  }

  /** La scheda in cache: l'ultima risposta del server con sopra le modifiche in volo di questo giro. */
  function redraw() {
    const { confirmed, pending, epoch } = state.current;
    if (!confirmed) return;
    const lead = pending.filter((write) => write.epoch === epoch).reduce((current, write) => write.optimistic(current), confirmed);
    queryClient.setQueryData<LeadCardResponse>(leadKeys.card(tabPart, leadId, view), (card) => (card ? { ...card, lead } : card));
  }

  function run(write: Omit<Write, "id" | "epoch">): Promise<LeadWriteResponse> {
    state.current.counter += 1;
    return mutation.mutateAsync({ ...write, id: state.current.counter, epoch: state.current.epoch });
  }

  const path = leadPath(leadId);
  return {
    save: (edit: LeadEdit) =>
      run({
        optimistic: edit.optimistic,
        reloadAfter: edit.reloadAfter,
        send: (version) => apiRequest("PATCH", path, { schema: leadWriteResponseSchema, body: { version, changes: edit.changes }, tab }),
      }),
    changeOwner: (body: Omit<ChangeOwnerBody, "version">) =>
      run({
        optimistic: unchanged,
        reloadAfter: true,
        send: (version) => apiRequest("POST", `${path}/owner`, { schema: leadWriteResponseSchema, body: { version, ...body }, tab }),
      }),
    setTags: (tagIds: string[]) =>
      run({ optimistic: unchanged, send: (version) => apiRequest("PUT", `${path}/tags`, { schema: leadWriteResponseSchema, body: { version, tagIds }, tab }) }),
    setWhatsAppOptOut: (on: boolean) =>
      run({
        optimistic: unchanged,
        send: async (version) => {
          const response = await apiRequest("PUT", `${path}/whatsapp-opt-out`, { schema: whatsAppOptOutResponseSchema, body: { on, expectedVersion: version }, tab });
          // «WhatsApp» e «Invia WhatsApp» si spengono (o si riaccendono) con il motivo della risposta; il Lead lo scrive la coda.
          queryClient.setQueryData<LeadCardResponse>(leadKeys.card(tabPart, leadId, view), (card) => (card ? { ...card, whatsapp: response.whatsapp } : card));
          return { lead: response.lead };
        },
      }),
  };
}

export type LeadCardWrites = ReturnType<typeof useLeadCardWrites>;

/** I rifiuti dopo cui la sezione della sequenza non è più quella del server: la scheda si rilegge. */
const SEQUENCE_STALE_CODES: ReadonlySet<string> = new Set(["version_conflict", "sequence_not_open", "sequence_cannot_restart", "permission_denied", "lead_not_found"]);

/**
 * Un comando della sequenza di chiamate dalla scheda (piano M5, Task 12; `POST /api/leads/:leadId/sequence/…`, Task 11): con la
 * versione della sequenza letta (`409 version_conflict` se è cambiata). La risposta è la sequenza com'è dopo, e va subito nella
 * scheda; la scheda («Prossimo passo») e la Timeline (l'evento con il nome di chi l'ha fatto) si rileggono, l'elenco e il menu
 * delle Smart List anche (il filtro «Sequenza di chiamate»). Un rifiuto per una sequenza cambiata nel frattempo rilegge la scheda.
 */
function useSequenceCommand<Body>(tab: TabContext, leadId: string, view: string | null, action: "stop" | "restart" | "answered") {
  const queryClient = useQueryClient();
  const tabPart = tabKey(tab);
  const key = leadKeys.card(tabPart, leadId, view);
  return useMutation({
    mutationFn: (body: Body) => apiRequest("POST", `${leadPath(leadId)}/sequence/${action}`, { schema: callSequenceResponseSchema, body, tab }),
    onSuccess: async ({ sequence }) => {
      await queryClient.cancelQueries({ queryKey: key, exact: true });
      queryClient.setQueryData<LeadCardResponse>(key, (card) => (card ? { ...card, sequence, nextStep: sequence.state === "open" ? card.nextStep : null } : card));
      void queryClient.invalidateQueries({ queryKey: key, exact: true });
      void queryClient.invalidateQueries({ queryKey: leadKeys.timelines(tabPart, leadId, view) });
      void queryClient.invalidateQueries({ queryKey: leadKeys.lists(tabPart) });
      refreshSmartListMenu(queryClient, tab);
    },
    onError: (error) => {
      if (error instanceof ApiRequestError && SEQUENCE_STALE_CODES.has(error.code)) void queryClient.invalidateQueries({ queryKey: key, exact: true });
    },
  });
}

/** «Ferma la sequenza». */
export function useStopSequence(tab: TabContext, leadId: string, view: string | null) {
  return useSequenceCommand<SequenceActionBody>(tab, leadId, view, "stop");
}

/** «Riparti con la sequenza», anche per un Lead che non ne ha mai avuta una (questione 256). */
export function useRestartSequence(tab: TabContext, leadId: string, view: string | null) {
  return useSequenceCommand<SequenceActionBody>(tab, leadId, view, "restart");
}

/** «Il Lead ha risposto», con come. */
export function useMarkAnswered(tab: TabContext, leadId: string, view: string | null) {
  return useSequenceCommand<MarkAnsweredBody>(tab, leadId, view, "answered");
}

/**
 * La finestra «Richiama dopo» di un Lead (`GET /api/leads/:leadId/callback-slots`, piano M5, D13; Task 17): gli orari li calcola
 * il server da adesso, nel fuso dell'azienda, quindi si rileggono a ogni apertura della finestra.
 */
export function callbackSlotsQuery(tab: TabContext, leadId: string, view: string | null) {
  return queryOptions({
    queryKey: ["scheda", tabKey(tab), "leads", "callback-slots", view, leadId] as const,
    queryFn: ({ signal }) => apiRequest("GET", `${leadPath(leadId)}/callback-slots`, { schema: callbackSlotsResponseSchema, tab, signal }),
    staleTime: 0,
    gcTime: 0,
  });
}

/** I rifiuti dopo cui il richiamo aperto della scheda non è più quello del server: la scheda si rilegge. */
const TASK_STALE_CODES: ReadonlySet<string> = new Set(["task_exists", "task_not_found", "version_conflict", "invalid_state", "permission_denied", "lead_not_found"]);

/**
 * Un richiamo fissato o spostato (piano M5, Task 17): il Task com'è dopo va subito nella scheda (`openTask` e «Prossimo passo»),
 * poi la scheda, la Timeline (l'evento del richiamo) e l'agenda dei Task (piano M5, Task 18) si rileggono. Un rifiuto per un richiamo cambiato nel frattempo (`409
 * task_exists` di un collega, «Sposta» con una versione vecchia, un Task non più aperto) rilegge la scheda: la finestra mostra
 * il richiamo di adesso.
 */
function useTaskWrite<Input>(tab: TabContext, leadId: string, view: string | null, send: (input: Input) => Promise<{ task: TaskView }>) {
  const queryClient = useQueryClient();
  const tabPart = tabKey(tab);
  const key = leadKeys.card(tabPart, leadId, view);
  return useMutation({
    mutationFn: send,
    onSuccess: async ({ task }) => {
      await queryClient.cancelQueries({ queryKey: key, exact: true });
      const open = task.status === "open";
      queryClient.setQueryData<LeadCardResponse>(key, (card) =>
        card
          ? {
              ...card,
              openTask: open ? task : null,
              nextStep: open ? { kind: "callback", taskId: task.id, dueAt: task.dueAt, agreedWithLead: task.agreedWithLead } : card.nextStep,
            }
          : card,
      );
      void queryClient.invalidateQueries({ queryKey: key, exact: true });
      void queryClient.invalidateQueries({ queryKey: leadKeys.timelines(tabPart, leadId, view) });
      void queryClient.invalidateQueries({ queryKey: taskKeys.all(tabPart) });
      // Il prossimo richiamo è anche una colonna degli elenchi e un filtro delle Smart List (rilievo di Codex sulla #107).
      void queryClient.invalidateQueries({ queryKey: leadKeys.lists(tabPart) });
      refreshSmartListMenu(queryClient, tab);
    },
    onError: async (error) => {
      if (error instanceof ApiRequestError && TASK_STALE_CODES.has(error.code)) await queryClient.refetchQueries({ queryKey: key, exact: true });
    },
  });
}

/** «Richiama dopo» (`POST /api/leads/:leadId/tasks`): lo stesso `requestId` risponde lo stesso Task (ruling Q54). */
export function useCreateCallback(tab: TabContext, leadId: string, view: string | null) {
  return useTaskWrite(tab, leadId, view, (body: CreateCallbackBody) =>
    apiRequest("POST", `${leadPath(leadId)}/tasks`, { schema: taskResponseSchema, body, tab }),
  );
}

/** «Sposta» del richiamo aperto (`PATCH /api/tasks/:taskId`), con la versione letta. */
export function useRescheduleTask(tab: TabContext, leadId: string, view: string | null) {
  return useTaskWrite(tab, leadId, view, ({ taskId, body }: { taskId: string; body: RescheduleTaskBody }) =>
    apiRequest("PATCH", `/api/tasks/${encodeURIComponent(taskId)}`, { schema: taskResponseSchema, body, tab }),
  );
}

/** Chi può essere owner di un Lead (`GET /api/lead-owners/options`), per chi ha la funzione `change_owner`. */
export function leadOwnerOptionsQuery(tab: TabContext) {
  return queryOptions({
    queryKey: ["scheda", tabKey(tab), "lead-owners", "options"],
    queryFn: async ({ signal }) => (await apiRequest("GET", "/api/lead-owners/options", { schema: leadOwnerOptionsResponseSchema, tab, signal })).owners,
    staleTime: 60_000,
  });
}

/** I comuni che rispondono a una ricerca (al massimo otto, dal server: il catalogo ISTAT non entra nel browser). */
export function municipalitiesQuery(tab: TabContext, q: string) {
  return queryOptions({
    queryKey: ["scheda", tabKey(tab), "geography", "municipalities", q],
    queryFn: async ({ signal }) =>
      (await apiRequest("GET", `/api/geography/municipalities?${new URLSearchParams({ q }).toString()}`, { schema: municipalitiesResponseSchema, tab, signal }))
        .municipalities,
    // Il catalogo dei comuni cambia soltanto con una versione nuova del server.
    staleTime: Infinity,
  });
}

/**
 * «Nuovo Lead» (`POST /api/leads`): la risposta porta soltanto l'identificativo, la scheda si apre con la sua lettura.
 * Gli elenchi dell'azienda si rileggono, perché il Lead nuovo può entrarci, e il menu delle Smart List con i suoi numeri e
 * le sue novità: un Lead nuovo è proprio una novità (questione aperta 182).
 */
export function useCreateLead(tab: TabContext) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: CreateLeadBody) => apiRequest("POST", "/api/leads", { schema: createLeadResponseSchema, body, tab }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["scheda", tabKey(tab), "leads", "list"] });
      refreshSmartListMenu(queryClient, tab);
    },
  });
}

// ---------------------------------------------------------------------------------------------------------------
// Le Impostazioni della scheda (piano M2 Task 20): «Campi della scheda» e «Chi vede i Lead».

/**
 * Il modello pubblicato della scheda e i campi dell'azienda, anche archiviati (`GET /api/settings/lead-fields`). Chi non
 * modifica i campi ne vede l'uso contato sui soli Lead che vede: la chiave porta la Vista Commerciale.
 */
export function leadFieldsQuery(tab: TabContext) {
  return queryOptions({
    queryKey: ["scheda", tabKey(tab), "lead-fields", commercialViewOf(tab)],
    queryFn: ({ signal }) => apiRequest("GET", "/api/settings/lead-fields", { schema: leadFieldsResponseSchema, tab, signal }),
  });
}

/**
 * Pubblica una versione nuova dei «Campi della scheda». La promessa si chiude quando il modello è già stato riletto,
 * così la pagina non mostra la versione vecchia fra la risposta e la rilettura; le schede dei Lead aperte si rileggono
 * (il modello è cambiato) senza aspettarle. Dopo un rifiuto il modello si rilegge lo stesso: un conflitto di versione
 * porta così la versione più nuova.
 */
export function usePublishLeadFields(tab: TabContext) {
  const queryClient = useQueryClient();
  const tabPart = tabKey(tab);
  return useMutation({
    mutationFn: (body: PublishLeadFieldsBody) => apiRequest("POST", "/api/settings/lead-fields/publish", { schema: publishLeadFieldsResponseSchema, body, tab }),
    onSuccess: () => {
      // Le schede aperte e il catalogo dei campi di «Filtri» seguono il modello nuovo; una lista con una condizione su un
      // campo archiviato la perde, e il menu rilegge i numeri.
      for (const part of ["card", "catalog"]) void queryClient.invalidateQueries({ queryKey: ["scheda", tabPart, "leads", part] });
      refreshSmartListMenu(queryClient, tab);
    },
    onSettled: () => queryClient.invalidateQueries({ queryKey: leadFieldsQuery(tab).queryKey }),
  });
}

/** «Tutti i Lead», «Soltanto i suoi» oppure «misto», e se chi guarda la può cambiare (`GET /api/settings/lead-visibility`). */
export function leadVisibilityQuery(tab: TabContext) {
  return queryOptions({
    queryKey: ["scheda", tabKey(tab), "lead-visibility"],
    queryFn: ({ signal }) => apiRequest("GET", "/api/settings/lead-visibility", { schema: leadVisibilityResponseSchema, tab, signal }),
  });
}

/**
 * Salva «Chi vede i Lead»: la risposta prende il posto della copia in cache. La scelta riscrive i ruoli di livello 1,
 * quindi si rileggono i ruoli e ogni lettura dei Lead dell'azienda (il loro perimetro può essere cambiato).
 */
export function useSaveLeadVisibility(tab: TabContext) {
  const queryClient = useQueryClient();
  const tabPart = tabKey(tab);
  return useMutation({
    mutationFn: (body: LeadVisibilityBody) => apiRequest("PUT", "/api/settings/lead-visibility", { schema: leadVisibilityResponseSchema, body, tab }),
    onSuccess: (response) => {
      queryClient.setQueryData(leadVisibilityQuery(tab).queryKey, response);
      for (const queryKey of [["scheda", tabPart, "leads"], ["scheda", tabPart, "roles"]]) void queryClient.invalidateQueries({ queryKey });
      refreshSmartListMenu(queryClient, tab);
    },
    onError: () => void queryClient.invalidateQueries({ queryKey: leadVisibilityQuery(tab).queryKey }),
  });
}

// ---------------------------------------------------------------------------------------------------------------
// I Lead di chi viene sospeso o archiviato (piano M2 Task 22, rotte del Task 10): i conteggi per owner della pagina Team
// e «Riassegnali agli altri User, a turno».

/**
 * Quanti Lead non archiviati ha ogni owner (`GET /api/lead-owners/counts`, con `manage_team`), contati dal server dentro
 * il perimetro della richiesta: con la Vista Commerciale, quello del Commerciale osservato. La chiave porta la vista.
 */
export function leadOwnerCountsQuery(tab: TabContext) {
  return queryOptions({
    queryKey: ["scheda", tabKey(tab), "lead-owners", "counts", commercialViewOf(tab)],
    queryFn: async ({ signal }) => {
      const { counts } = await apiRequest("GET", "/api/lead-owners/counts", { schema: ownerCountsResponseSchema, tab, signal });
      return new Map(counts.map((entry) => [entry.membershipId, entry.leads]));
    },
  });
}

/**
 * Riassegna a turno agli User attivi i Lead di chi è sospeso o archiviato (`POST /api/memberships/:id/reassign-leads`).
 * Riuscita o rifiutata, si rileggono i conteggi e le letture dei Lead della scheda, il cui owner può essere cambiato.
 */
export function useReassignLeads(tab: TabContext) {
  const queryClient = useQueryClient();
  const tabPart = tabKey(tab);
  return useMutation({
    mutationFn: (membershipId: string) =>
      apiRequest("POST", `/api/memberships/${encodeURIComponent(membershipId)}/reassign-leads`, { schema: reassignLeadsResponseSchema, body: { mode: "round_robin" }, tab }),
    onSettled: () => {
      for (const queryKey of [["scheda", tabPart, "lead-owners"], ["scheda", tabPart, "leads"]]) void queryClient.invalidateQueries({ queryKey });
      refreshSmartListMenu(queryClient, tab);
    },
  });
}
